export interface TaxBracket {
  min: number;
  max: number;
  rate: number;
}

export interface TaxResult {
  grossSalary: number;
  federalTax: number;
  stateTax: number;
  localTax: number;
  socialSecurity: number;
  medicare: number;
  nationalInsurance: number;
  totalTax: number;
  netSalary: number;
  effectiveRate: number;
  breakdown: TaxBreakdownItem[];
}

export interface TaxBreakdownItem {
  category: string;
  amount: number;
  rate: number;
  details?: string;
}

export class TaxEngine {
  private locationData: any;
  private countryData: any;

  constructor(locationData: any, countryData: any) {
    this.locationData = locationData;
    this.countryData = countryData;
  }

  calculate(grossSalary: number, filingStatus: string = 'single'): TaxResult {
    const taxSystem = this.countryData.tax_system;

    if (taxSystem === 'federal_state_local') {
      return this.calculateUSATax(grossSalary, filingStatus);
    } else if (taxSystem === 'national_insurance') {
      return this.calculateUKTax(grossSalary);
    }

    return this.getEmptyResult(grossSalary);
  }

  private calculateUSATax(grossSalary: number, filingStatus: string): TaxResult {
    const breakdown: TaxBreakdownItem[] = [];

    // Federal Tax
    const standardDeduction = this.countryData.federal_tax.standard_deduction;
    const taxableIncome = Math.max(0, grossSalary - standardDeduction);
    const federalTax = this.calculateBracketTax(
      taxableIncome,
      this.countryData.federal_tax.brackets
    );
    breakdown.push({
      category: 'Federal Tax',
      amount: federalTax,
      rate: federalTax / grossSalary,
      details: `Taxable income: $${taxableIncome.toLocaleString()} (after $${standardDeduction.toLocaleString()} standard deduction)`
    });

    // Social Security
    const socialSecurityBase = Math.min(
      grossSalary,
      this.countryData.federal_tax.social_security_cap
    );
    const socialSecurity = socialSecurityBase * this.countryData.federal_tax.social_security_rate;
    breakdown.push({
      category: 'Social Security',
      amount: socialSecurity,
      rate: this.countryData.federal_tax.social_security_rate,
      details: `6.2% on first $${this.countryData.federal_tax.social_security_cap.toLocaleString()}`
    });

    // Medicare
    const medicare = grossSalary * this.countryData.federal_tax.medicare_rate;
    breakdown.push({
      category: 'Medicare',
      amount: medicare,
      rate: this.countryData.federal_tax.medicare_rate,
      details: '1.45% on all income'
    });

    // State Tax
    const stateData = this.getStateData();
    const stateTax = stateData?.state_tax?.brackets
      ? this.calculateBracketTax(grossSalary, stateData.state_tax.brackets)
      : 0;
    if (stateTax > 0) {
      breakdown.push({
        category: 'State Tax',
        amount: stateTax,
        rate: stateTax / grossSalary,
        details: `${stateData.name} state income tax`
      });
    }

    // Local Tax
    const localTaxRate = this.locationData.local_tax_rate || 0;
    const localTax = grossSalary * localTaxRate;
    if (localTax > 0) {
      breakdown.push({
        category: 'Local Tax',
        amount: localTax,
        rate: localTaxRate,
        details: `${this.locationData.name} local income tax`
      });
    }

    const totalTax = federalTax + socialSecurity + medicare + stateTax + localTax;
    const netSalary = grossSalary - totalTax;

    return {
      grossSalary,
      federalTax,
      stateTax,
      localTax,
      socialSecurity,
      medicare,
      nationalInsurance: 0,
      totalTax,
      netSalary,
      effectiveRate: totalTax / grossSalary,
      breakdown
    };
  }

  private calculateUKTax(grossSalary: number): TaxResult {
    const breakdown: TaxBreakdownItem[] = [];

    // Income Tax
    const incomeTax = this.calculateBracketTax(
      grossSalary,
      this.countryData.income_tax.brackets
    );
    breakdown.push({
      category: 'Income Tax',
      amount: incomeTax,
      rate: incomeTax / grossSalary,
      details: `Personal allowance: £${this.countryData.income_tax.personal_allowance.toLocaleString()}`
    });

    // National Insurance
    const nationalInsurance = this.calculateBracketTax(
      grossSalary,
      this.countryData.national_insurance.brackets
    );
    breakdown.push({
      category: 'National Insurance',
      amount: nationalInsurance,
      rate: nationalInsurance / grossSalary,
      details: 'Employee NI contributions'
    });

    const totalTax = incomeTax + nationalInsurance;
    const netSalary = grossSalary - totalTax;

    return {
      grossSalary,
      federalTax: incomeTax,
      stateTax: 0,
      localTax: 0,
      socialSecurity: 0,
      medicare: 0,
      nationalInsurance,
      totalTax,
      netSalary,
      effectiveRate: totalTax / grossSalary,
      breakdown
    };
  }

  private calculateBracketTax(income: number, brackets: TaxBracket[]): number {
    let tax = 0;
    let previousMax = 0;

    for (const bracket of brackets) {
      if (income <= previousMax) break;

      const taxableInBracket = Math.min(income, bracket.max) - Math.max(previousMax, bracket.min - 1);
      if (taxableInBracket > 0) {
        tax += taxableInBracket * bracket.rate;
      }

      previousMax = bracket.max;
    }

    return tax;
  }

  private getStateData(): any {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    if (pathParts.length >= 2 && this.countryData.states) {
      const stateSlug = pathParts[1];
      return this.countryData.states[stateSlug];
    }
    return null;
  }

  private getEmptyResult(grossSalary: number): TaxResult {
    return {
      grossSalary,
      federalTax: 0,
      stateTax: 0,
      localTax: 0,
      socialSecurity: 0,
      medicare: 0,
      nationalInsurance: 0,
      totalTax: 0,
      netSalary: grossSalary,
      effectiveRate: 0,
      breakdown: []
    };
  }

  getBracketDetails(income: number, brackets: TaxBracket[]): Array<{bracket: TaxBracket, taxInBracket: number, isActive: boolean}> {
    const details: Array<{bracket: TaxBracket, taxInBracket: number, isActive: boolean}> = [];
    let previousMax = 0;

    for (const bracket of brackets) {
      const taxableInBracket = Math.min(income, bracket.max) - Math.max(previousMax, bracket.min - 1);
      const taxInBracket = taxableInBracket > 0 ? taxableInBracket * bracket.rate : 0;
      const isActive = income > previousMax && income >= bracket.min;

      details.push({
        bracket,
        taxInBracket,
        isActive
      });

      previousMax = bracket.max;
    }

    return details;
  }
}